import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type CreateParams,
  type CreateResult,
  type DataProvider,
  type GetListParams,
  type GetManyParams,
  type Identifier,
  type RaRecord,
  type ResourceCallbacks,
} from "ra-core";
import fakeRestDataProvider from "ra-data-fakerest";
import type {
  AdvisorAvailabilityBlock,
  AssignLeadResult,
  LeadAssignment,
  LeadAssignmentEligibleAdvisor,
  LeadAssignmentRule,
  ContactNote,
  Deal,
  DealNote,
  DeveloperFiscalDocument,
  EvaluateLeadAlertsResult,
  FeedbackReportInput,
  FunnelMetrics,
  FunnelMetricsFilters,
  LogDealActivityInput,
  LogDealActivityResult,
  RAFile,
  Sale,
  SalesTool,
  SalesToolFileMetadata,
  SalesFormData,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import {
  buildFiscalDocumentStoragePath,
  computeFileSha256Hex,
  DEVELOPER_FISCAL_DOCUMENTS_BUCKET,
  FISCAL_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS,
  validateFiscalDocumentFile,
} from "../commons/developerFiscalDocuments";
import {
  buildSalesToolStoragePath,
  SALES_TOOL_SIGNED_URL_EXPIRY_SECONDS,
  SALES_TOOLS_BUCKET,
  salesToolsDeleteCallbacks,
  validateSalesToolFile,
} from "../commons/salesToolFiles";
import {
  isExportRequest,
  isExportResource,
  type ExportableResource,
} from "../commons/exportResources";
import { transformFilter } from "../fakerest/internal/transformFilter";
import { normalizeOptionListFilter } from "../../opciones/opcionRules";
import { createSupabaseAiCopilotMethods } from "./aiCopilot";
import { getIsInitialized, invalidateCachedSale } from "./authProvider";
import { createSupabaseCarouselMethods } from "./carousel";
import { createSupabaseInternalNoticesMethods } from "./internalNotices";
import { createSupabaseWhatsAppMethods } from "./whatsapp";
import { createSupabaseWhatsAppInboxMethods } from "./whatsappInbox";
import { createSupabaseWhatsAppUnclassifiedMethods } from "./whatsappUnclassified";
import { createSupabaseLeadAssignmentConfigMethods } from "./leadAssignmentConfig";
import { createSupabaseContactExpedienteMethods } from "./contactExpediente";
import { createSupabaseDocumentsMethods } from "./documents";
import {
  createSupabaseExpensesMethods,
  getExpenseMonthlyTotals,
  supabaseExpensesCallbacks,
} from "./expenses";
import { createSupabaseInvoicingMethods } from "./invoicing";
import { createSupabaseMetaMethods } from "./metaAttribution";
import { createSupabaseOperationsMethods } from "./operations";
import { createSupabaseQuoterMethods } from "./quoter";
import { insertFeedbackReport } from "./feedbackReports";
import { getSupabaseClient } from "./supabase";

const EXPORT_DENIED_MESSAGE =
  "Solo direccion, lead manager o administracion pueden exportar";

/**
 * Almikia Epic E3b (TASK-066/071): route a flagged export read through the
 * matching `export_<resource>()` RPC (server-enforced role check, see
 * ADR-ee2f59a9-TASK-066-server-side-export-enforcement.md) instead of the
 * paginated REST list read. The RPC is a fixed, zero-argument function --
 * it returns every row RLS lets the caller see, unfiltered -- so filtering,
 * sorting and pagination are re-applied in-memory afterwards, reusing the
 * exact same engine (`ra-data-fakerest` + the existing `transformFilter`
 * postgrest-operator adapter) the FakeRest provider already relies on, so
 * the CSV keeps the same columns/rows as a normal read for the same filters.
 */
// PostgREST caps a single RPC response at its configured max_rows (1000 here),
// so an export must page through the RPC rather than issue one unranged call,
// or any RLS-visible row past the cap is silently dropped from the export.
// Exported for the pagination unit test below/alongside this module.
export const EXPORT_PAGE_SIZE = 1000;

export const fetchExportRows = async (resource: ExportableResource) => {
  const rows: Record<string, unknown>[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await getSupabaseClient()
      .rpc(`export_${resource}`)
      .order("id")
      .range(from, from + EXPORT_PAGE_SIZE - 1);

    if (error) {
      console.error(`export_${resource}.error`, error);
      throw new Error(EXPORT_DENIED_MESSAGE);
    }

    const page = (data ?? []) as Record<string, unknown>[];
    rows.push(...page);

    if (page.length < EXPORT_PAGE_SIZE) {
      break;
    }

    from += EXPORT_PAGE_SIZE;
  }

  return rows;
};

const getExportListResult = async (
  resource: ExportableResource,
  params: GetListParams,
) => {
  const rows = await fetchExportRows(resource);
  const localProvider = fakeRestDataProvider({ [resource]: rows }, false);
  return localProvider.getList(resource, {
    ...params,
    filter: transformFilter(params.filter) ?? {},
  });
};

const getExportManyResult = async (
  resource: ExportableResource,
  params: GetManyParams,
) => {
  const rows = await fetchExportRows(resource);
  const localProvider = fakeRestDataProvider({ [resource]: rows }, false);
  return localProvider.getMany(resource, params);
};

/**
 * Resources whose primary key is not `id` (otherwise ra-data-postgrest sorts by
 * a missing `id` column and PostgREST answers 400).
 * meta_lead_ingestions: leadgen_id; quoter_plan_approvers: sales_id.
 */
export const SUPABASE_PRIMARY_KEYS = new Map<string, string[]>([
  ["meta_lead_ingestions", ["leadgen_id"]],
  ["quoter_plan_approvers", ["sales_id"]],
]);

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
    primaryKeys: SUPABASE_PRIMARY_KEYS,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

/**
 * Uploads a tool file to the private `sales-tools` bucket under a NEW unique
 * path (never overwrites) and returns the metadata to store on the row. The
 * path does not depend on the row id, so a new tool can be inserted with its
 * file in one write.
 */
const uploadSalesToolObject = async (
  file: File,
): Promise<SalesToolFileMetadata> => {
  const validation = validateSalesToolFile(file);
  if (!validation.valid) {
    throw Object.assign(new Error(validation.reason), {
      code: validation.reason,
    });
  }

  const storagePath = buildSalesToolStoragePath(file.type);
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(SALES_TOOLS_BUCKET)
    .upload(storagePath, file, { upsert: false, contentType: file.type });

  if (uploadError) {
    console.error("uploadSalesToolObject.uploadError", uploadError);
    throw new Error("Failed to upload the sales tool file");
  }

  return {
    storage_path: storagePath,
    file_name: file.name,
    mime_type: file.type,
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async create<
      RecordType extends Omit<RaRecord, "id"> = any,
      ResultRecordType extends RaRecord = RecordType & { id: Identifier },
    >(
      resource: string,
      params: CreateParams<any>,
    ): Promise<CreateResult<ResultRecordType>> {
      // Almikia Epic PL (TASK-061): insert-only, no read-back (no SELECT policy).
      if (resource === "feedback_reports") {
        return (await insertFeedbackReport(
          params.data as unknown as FeedbackReportInput,
        )) as unknown as CreateResult<ResultRecordType>;
      }
      return baseDataProvider.create<RecordType, ResultRecordType>(
        resource,
        params,
      );
    },
    async getList(resource: string, params: GetListParams) {
      if (isExportRequest(params.meta) && isExportResource(resource)) {
        return getExportListResult(resource, params);
      }
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "operations") {
        // Almikia Epic E4 (TASK-027): reads go through the summary view.
        return baseDataProvider.getList("operations_summary", params);
      }
      if (resource === "receivables" || resource === "payables") {
        // Almikia Epic E6 (TASK-039): reads go through the summary views.
        return baseDataProvider.getList(`${resource}_summary`, params);
      }
      if (resource === "expenses") {
        // Almikia Epic EG (TASK-043): reads go through the summary view; writes
        // go to the `expenses` table.
        return baseDataProvider.getList("expenses_summary", params);
      }
      if (resource === "expense_monthly_totals") {
        return getExpenseMonthlyTotals(
          (r, p) => baseDataProvider.getList(r, p),
          params,
        );
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }
      if (resource === "operations") {
        return baseDataProvider.getOne("operations_summary", params);
      }
      if (resource === "receivables" || resource === "payables") {
        return baseDataProvider.getOne(`${resource}_summary`, params);
      }
      if (resource === "expenses") {
        return baseDataProvider.getOne("expenses_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },
    async getMany(resource: string, params: GetManyParams) {
      // Same rationale as getList above -- the contacts BulkExportButton
      // fetches only the selected ids via getMany.
      if (isExportRequest(params.meta) && isExportResource(resource)) {
        return getExportManyResult(resource, params);
      }
      return baseDataProvider.getMany(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(errorDetails?.message || "Failed to create the user"),
          {
            code: errorDetails?.code,
            email: errorDetails?.email,
            advisor_code: errorDetails?.advisor_code ?? body.advisor_code,
          },
        );
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        secondary_emails,
        first_name,
        last_name,
        role,
        avatar,
        disabled,
        business_arm,
        reports_to,
        advisor_code,
      } = data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            secondary_emails,
            first_name,
            last_name,
            role,
            disabled,
            avatar,
            business_arm,
            reports_to,
            advisor_code,
          },
        });

      if (!updatedData || error) {
        console.error("salesUpdate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(
            errorDetails?.message || "Failed to update account manager",
          ),
          {
            code: errorDetails?.code,
            email: errorDetails?.email,
            advisor_code: errorDetails?.advisor_code ?? advisor_code,
          },
        );
      }

      // Almikia Epic E3b (TASK-067): the signed-in user's identity is cached
      // in localStorage (authProvider.ts) to avoid an extra request on every
      // page load. Bust it on any successful save so a subsequent
      // getIdentity() -- including after a page reload -- reflects the new
      // value instead of serving the stale cached one (matters most for her
      // own advisor_code: the sign-in prompt/profile page must stop asking
      // once she has saved it). Harmless when the edited record isn't the
      // caller's own (e.g. direccion editing a team member): it just costs
      // one refetch next time.
      // See adr/ADR-ee2f59a9-TASK-067-nonblocking-prompt-and-cache-invalidation.md
      invalidateCachedSale();

      return updatedData.data;
    },
    /**
     * Almikia Epic EC2c: the onboarding accumulated sales are written ONLY
     * through the Direccion-only RPC (never the service-role users function,
     * which the DB guard refuses). The RPC's Spanish error text is forwarded.
     */
    async setAccumulatedSalesAtOnboarding(
      id: Identifier,
      value: number,
    ): Promise<void> {
      const { error } = await getSupabaseClient().rpc(
        "set_accumulated_sales_at_onboarding",
        { p_sales_id: id, p_value: value },
      );
      if (error) {
        console.error("set_accumulated_sales_at_onboarding.error", error);
        throw new Error(
          error.message || "No se pudieron guardar las ventas acumuladas",
        );
      }
    },
    /**
     * Almikia Epic EC2c: the phones of up to 200 team members in one
     * `team_member_contacts` call. The RPC's access control returns nothing
     * to a caller without an active sales row. Never logs the payload (it
     * carries phones); only the error code is reported.
     */
    async getTeamContacts(
      salesIds: Identifier[],
    ): Promise<{ sales_id: Identifier; phone: string }[]> {
      const { data, error } = await getSupabaseClient().rpc(
        "team_member_contacts",
        { p_sales_ids: salesIds.map(Number) },
      );
      if (error) {
        console.error("team_member_contacts.error", error.code);
        throw new Error("No se pudieron cargar los contactos del equipo");
      }
      return (data ?? []) as { sales_id: Identifier; phone: string }[];
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async checkClientRfcDuplicate(
      rfc: string,
      excludingClientId?: Identifier,
    ): Promise<boolean> {
      // Almikia Epic E3b, owner answer A7 (TASK-069/073): calls the
      // SECURITY DEFINER `is_client_rfc_duplicate()` RPC, which returns only
      // a boolean, never any of the other client's data. supabase-js sends
      // RPC params in the POST body (never the URL/query string), and never
      // echoes the RFC being checked.
      const { data, error } = await getSupabaseClient().rpc(
        "is_client_rfc_duplicate",
        {
          p_rfc: rfc,
          p_excluding_client_id: excludingClientId ?? null,
        },
      );

      if (error) {
        console.error("is_client_rfc_duplicate.error", error);
        throw new Error("No se pudo verificar el RFC");
      }

      return Boolean(data);
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async confirmTeamRosterMatch(rosterId: Identifier): Promise<void> {
      // Almikia Epic E3b-2 (TASK-083/085): thin wrapper over
      // `confirm_team_roster_match()`, a SECURITY DEFINER RPC that is the
      // sole source of authority here (see
      // adr/ADR-ee2f59a9-TASK-083-roster-claim-and-confirm.md) -- the UI's
      // own `getTeamRosterClaimAuthorization` only mirrors it. `error.message`
      // is the Spanish text the function raises (e.g. "No tienes permiso
      // para confirmar esta fila del roster"), so it is forwarded as-is
      // rather than replaced with a generic message.
      const { error } = await getSupabaseClient().rpc(
        "confirm_team_roster_match",
        { p_roster_id: rosterId },
      );
      if (error) {
        console.error("confirm_team_roster_match.error", error);
        throw new Error(error.message || "No se pudo confirmar esta clave");
      }
    },
    async rejectTeamRosterMatch(rosterId: Identifier): Promise<void> {
      const { error } = await getSupabaseClient().rpc(
        "reject_team_roster_match",
        { p_roster_id: rosterId },
      );
      if (error) {
        console.error("reject_team_roster_match.error", error);
        throw new Error(error.message || "No se pudo rechazar esta clave");
      }
    },
    async logDealActivity(
      input: LogDealActivityInput,
    ): Promise<LogDealActivityResult> {
      // Almikia Epic EA (TASK-110): a thin wrapper over the SECURITY INVOKER
      // `log_deal_activity()` RPC (DATA CONTRACT EA (7)) -- every insert/
      // update it performs goes through the existing deal_notes/tasks RLS,
      // so this function is not the authority, just a forwarder. Never logs
      // `input.note` -- only the error object, like every other RPC wrapper
      // here.
      const { data, error } = await getSupabaseClient().rpc(
        "log_deal_activity",
        {
          p_deal_id: input.dealId,
          p_activity_type_id: input.activityTypeId,
          p_activity_result_id: input.activityResultId,
          p_note: input.note ?? null,
          p_next_due_at: input.nextDueAt ?? null,
        },
      );

      if (error || !data) {
        console.error("log_deal_activity.error", error);
        throw new Error(error?.message || "No se pudo registrar la actividad");
      }

      const json = data as {
        note_id: LogDealActivityResult["noteId"];
        task_id: LogDealActivityResult["taskId"];
        closed_task_ids: LogDealActivityResult["closedTaskIds"];
        next_step: LogDealActivityResult["nextStep"];
      };
      return {
        noteId: json.note_id,
        taskId: json.task_id ?? null,
        closedTaskIds: json.closed_task_ids ?? [],
        nextStep: json.next_step,
      };
    },
    async getFunnelMetrics(
      filters?: FunnelMetricsFilters,
    ): Promise<FunnelMetrics> {
      // Almikia Epic ER (TASK-002): a thin wrapper over the SECURITY INVOKER
      // `get_funnel_metrics()` RPC -- every figure it returns already went
      // through the caller's own RLS (see
      // adr/ADR-ee2f59a9-TASK-001-stage-entry-log.md for the underlying
      // stage-entry log this reads). `error.message` is the Spanish text the
      // function raises (e.g. the advisor-filter role gate), forwarded as-is
      // like `confirmTeamRosterMatch` above.
      const { data, error } = await getSupabaseClient().rpc(
        "get_funnel_metrics",
        {
          p_advisor_id: filters?.advisorId ?? null,
          p_project_id: filters?.projectId ?? null,
          p_lead_type: filters?.leadType ?? null,
        },
      );

      if (error || !data) {
        console.error("get_funnel_metrics.error", error);
        throw new Error(
          error?.message || "No se pudo calcular el embudo de conversión",
        );
      }

      return data as FunnelMetrics;
    },
    async evaluateLeadAlerts(): Promise<EvaluateLeadAlertsResult> {
      // Almikia Epic ER (TASK-005): a thin wrapper over the SECURITY DEFINER
      // `evaluate_lead_alerts()` RPC -- carries no lead data either way (NFR:
      // security), just whether a full evaluation ran or was throttled.
      // `error.message` is the Spanish text the function raises (e.g. the
      // commercial-role gate), forwarded as-is like `getFunnelMetrics` above.
      const { data, error } = await getSupabaseClient().rpc(
        "evaluate_lead_alerts",
      );

      if (error || !data) {
        console.error("evaluate_lead_alerts.error", error);
        throw new Error(error?.message || "No se pudieron evaluar las alertas");
      }

      return data as EvaluateLeadAlertsResult;
    },
    async assignLead(
      dealId: Identifier,
      channel: string = "manual",
    ): Promise<AssignLeadResult> {
      // Almikia Epic EL (TASK-007): thin wrapper over the SECURITY DEFINER `assign_lead()`
      // RPC, the sole authority on who receives the lead -- the client never picks the
      // recipient. `error.message` is the Spanish text the function raises (role gate,
      // disabled rule, invalid channel), forwarded as-is like `getFunnelMetrics` above.
      const { data, error } = await getSupabaseClient().rpc("assign_lead", {
        p_deal_id: dealId,
        p_channel: channel,
      });

      if (error || !data) {
        console.error("assign_lead.error", error);
        throw new Error(error?.message || "No se pudo asignar el lead");
      }

      return data as AssignLeadResult;
    },
    // Almikia Epic EL (TASK-007): plain reads straight from the tables (RLS from TASK-006
    // decides who sees what).
    async getLeadAssignmentRules(): Promise<LeadAssignmentRule[]> {
      const { data, error } = await getSupabaseClient()
        .from("lead_assignment_rules")
        .select("*");
      if (error) throw new Error(error.message);
      return data as LeadAssignmentRule[];
    },
    async getLeadAssignmentEligibleAdvisors(
      projectId?: Identifier,
    ): Promise<LeadAssignmentEligibleAdvisor[]> {
      let query = getSupabaseClient()
        .from("lead_assignment_eligible_advisors")
        .select("*");
      if (projectId != null) query = query.eq("project_id", projectId);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      // Composite primary key (project_id, sales_id): build the synthetic `id`.
      return (data as Omit<LeadAssignmentEligibleAdvisor, "id">[]).map(
        (row) => ({ ...row, id: `${row.project_id}-${row.sales_id}` }),
      );
    },
    async getAdvisorAvailabilityBlocks(
      salesId?: Identifier,
    ): Promise<AdvisorAvailabilityBlock[]> {
      let query = getSupabaseClient()
        .from("advisor_availability_blocks")
        .select("*");
      if (salesId != null) query = query.eq("sales_id", salesId);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data as AdvisorAvailabilityBlock[];
    },
    async getLeadAssignments(dealId?: Identifier): Promise<LeadAssignment[]> {
      let query = getSupabaseClient()
        .from("lead_assignments")
        .select("*")
        .order("id", { ascending: false });
      if (dealId != null) query = query.eq("deal_id", dealId);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data as LeadAssignment[];
    },
    ...createSupabaseLeadAssignmentConfigMethods(),
    // Almikia Epic EL2 (TASK-073): typed reads of the BPO carousel section.
    ...createSupabaseCarouselMethods(),
    // Almikia Epic EW2 (TASK-079): WhatsApp conversation reads and send (edge function).
    ...createSupabaseWhatsAppMethods(),
    // Almikia Epic EW2 (TASK-080): inbox, lead badges, link and monthly counter.
    ...createSupabaseWhatsAppInboxMethods(),
    // Almikia Epic EW3 (TASK-095): 'Sin clasificar' tray, personal numbers and the permission switch.
    ...createSupabaseWhatsAppUnclassifiedMethods(),
    // Almikia Epic EN (TASK-091): internal notices settings, preferences and "Lo tomo".
    ...createSupabaseInternalNoticesMethods(),
    ...createSupabaseAiCopilotMethods(),
    ...createSupabaseQuoterMethods(),
    ...createSupabaseOperationsMethods(),
    ...createSupabaseDocumentsMethods(),
    // Almikia Epic EC2b (TASK-124): contact expediente RPCs, private bucket and signed URLs.
    ...createSupabaseContactExpedienteMethods(),
    ...createSupabaseInvoicingMethods(),
    ...createSupabaseExpensesMethods(),
    ...createSupabaseMetaMethods(),
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
    async uploadDeveloperFiscalDocument(
      developerId: Identifier,
      file: File,
    ): Promise<DeveloperFiscalDocument> {
      const validation = validateFiscalDocumentFile(file);
      if (!validation.valid) {
        throw Object.assign(new Error(validation.reason), {
          code: validation.reason,
        });
      }

      const storagePath = buildFiscalDocumentStoragePath(developerId);
      const sha256 = await computeFileSha256Hex(file);

      const { error: uploadError } = await getSupabaseClient()
        .storage.from(DEVELOPER_FISCAL_DOCUMENTS_BUCKET)
        .upload(storagePath, file, {
          upsert: false,
          contentType: "application/pdf",
        });

      if (uploadError) {
        console.error("uploadDeveloperFiscalDocument.uploadError", uploadError);
        throw new Error("Failed to upload the Constancia de Situacion Fiscal");
      }

      // ASSUMPTION: if the metadata insert below fails, the just-uploaded
      // storage object is orphaned (no delete policy on this private bucket
      // to clean it up here); this minimal upload path is superseded by E5.
      try {
        const { data } = await baseDataProvider.create(
          "developer_fiscal_documents",
          {
            data: {
              developer_id: developerId,
              storage_path: storagePath,
              sha256,
            },
          },
        );
        return data as DeveloperFiscalDocument;
      } catch (insertError) {
        console.error("uploadDeveloperFiscalDocument.insertError", insertError);
        throw new Error(
          "Failed to record the uploaded Constancia de Situacion Fiscal",
        );
      }
    },
    async getDeveloperFiscalDocumentUrl(
      document: DeveloperFiscalDocument,
    ): Promise<string> {
      const { data, error } = await getSupabaseClient()
        .storage.from(DEVELOPER_FISCAL_DOCUMENTS_BUCKET)
        .createSignedUrl(
          document.storage_path,
          FISCAL_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS,
        );

      if (error || !data) {
        console.error("getDeveloperFiscalDocumentUrl.error", error);
        throw new Error(
          "Failed to create a signed URL for the Constancia de Situacion Fiscal",
        );
      }

      return data.signedUrl;
    },
    /**
     * Uploads a tool file to the private `sales-tools` bucket under a NEW
     * unique path (never overwrites, never removes the previous object),
     * then points the tool row at it.
     */
    async uploadSalesToolFile(
      toolId: Identifier,
      file: File,
    ): Promise<SalesTool> {
      const metadata = await uploadSalesToolObject(file);
      const { data } = await baseDataProvider.update("sales_tools", {
        id: toolId,
        data: metadata,
        previousData: { id: toolId },
      });
      return data as SalesTool;
    },
    uploadSalesToolObject,
    /** Returns a short-lived signed URL for a tool file (never a public URL). */
    async getSalesToolFileUrl(tool: SalesTool): Promise<string> {
      if (!tool.storage_path) {
        throw new Error("The sales tool has no file");
      }
      const { data, error } = await getSupabaseClient()
        .storage.from(SALES_TOOLS_BUCKET)
        .createSignedUrl(
          tool.storage_path,
          SALES_TOOL_SIGNED_URL_EXPIRY_SECONDS,
        );

      if (error || !data) {
        console.error("getSalesToolFileUrl.error", error);
        throw new Error(
          "Failed to create a signed URL for the sales tool file",
        );
      }

      return data.signedUrl;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) =>
            uploadToBucket(fi, { storePathOnly: true }),
          ),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) =>
            uploadToBucket(fi, { storePathOnly: true }),
          ),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "second_last_name",
        "company_name",
        "title",
        "email_fts",
        "phone_fts",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "second_last_name",
      ])(params);
    },
  },
  {
    resource: "clients",
    // Almikia Epic E3b (TASK-064), owner answer A5: client search by name,
    // phone or email is allowed (RLS still limits which rows come back).
    // RFC and CURP are deliberately never included as search columns.
    beforeGetList: async (params) => {
      return applyFullTextSearch(["full_legal_name", "phone", "email"])(params);
    },
  },
  {
    resource: "property_options",
    // Almikia Epic EC (TASK-030): search by address; the list's `responsable`
    // filter (an advisor or AMK) maps onto the real columns.
    beforeGetList: async (params) => {
      return applyFullTextSearch(["address"])({
        ...params,
        filter: normalizeOptionListFilter(params.filter),
      });
    },
  },
  {
    resource: "inventory_items",
    // Almikia Epic EC2d (TASK-136): the read-only Inventario view; same address search and
    // `responsable` mapping as the options list. The range filters (`price@gte`, `area_m2@lte`,
    // `levels@gte`, ...) are plain PostgREST operators the adapter already forwards.
    beforeGetList: async (params) => {
      return applyFullTextSearch(["address"])({
        ...params,
        filter: normalizeOptionListFilter(params.filter),
      });
    },
  },
  {
    resource: "operations",
    // Almikia Epic E4 (TASK-027): search by operation id or contract folio.
    beforeGetList: async (params) => {
      return applyFullTextSearch(["id", "contract_folio"])(params);
    },
  },
  {
    resource: "receivables",
    // Almikia Epic E6 (TASK-040): search by operation id, project or client
    // (the text snapshots of `receivables_summary`).
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "operation_id",
        "project_name",
        "client_name",
      ])(params);
    },
  },
  {
    resource: "payables",
    // Almikia Epic E6 (TASK-041): search by operation id, beneficiary or
    // project (the text snapshots of `payables_summary`).
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "operation_id",
        "payee_name",
        "project_code",
      ])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
  {
    resource: "projects",
    // Almikia Epic E2 (TASK-010): SellableProjectInput's AutocompleteInput
    // sends a `q` filter on `projects`; map it to name/code the same way
    // other resources map `q` to their own searchable columns.
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "code"])(params);
    },
  },
  // Almikia Epic EH: tools are archived, never deleted.
  salesToolsDeleteCallbacks,
  // Almikia Epic EG (TASK-043): granted-columns-only writes, no delete.
  ...supabaseExpensesCallbacks,
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;
};

/**
 * Maps a list request's generic `q` filter onto an ilike search across the
 * given columns, the same way every searchable resource does it here. Pass
 * the exact column name to match (e.g. a view's `email_fts` aggregate for
 * contacts, or a plain `email` column for clients) — this helper does not
 * rename columns on the caller's behalf.
 */
export const applyFullTextSearch =
  (columns: string[]) => (params: GetListParams) => {
    if (!params.filter?.q) {
      return params;
    }
    const { q, ...filter } = params.filter;
    return {
      ...params,
      filter: {
        ...filter,
        "@or": columns.reduce(
          (acc, column) => ({
            ...acc,
            [`${column}@ilike`]: q,
          }),
          {},
        ),
      },
    };
  };

const uploadToBucket = async (
  fi: RAFile,
  { storePathOnly = false }: { storePathOnly?: boolean } = {},
) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  // Note attachments persist the object path only (the reader signs it on
  // render); logos and avatars are rendered from plain URLs, so keep the public URL.
  fi.path = filePath;
  if (storePathOnly) {
    fi.src = filePath;
  } else {
    const { data } = getSupabaseClient()
      .storage.from(ATTACHMENTS_BUCKET)
      .getPublicUrl(filePath);
    fi.src = data.publicUrl;
  }

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
